import { useState, useMemo, useCallback } from "react";

import { Badge, Box, Checkbox, Group, Stack, Text } from "@mantine/core";
import { IconClock, IconFlag } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { Link } from "components";
import { TodoConstants, RouteConstants } from "constant";

import { createTodoTypeLabels } from "./helpers";

export type { TodoType, TimeType, CompletionType } from "constant/todo";

export interface TodoUser {
  id: number;
  fullName: string;
  email: string;
  countryCode?: string;
  phoneNumber?: string;
  profileImage?: string;
  numberOfComments?: number;
  type: "CLIENT" | "EMPLOYEE";
}

export interface TodoItemData {
  id: number;
  title: string;
  description?: string;
  type: TodoConstants.TodoType;
  timeType: TodoConstants.TimeType;
  completionType: TodoConstants.CompletionType;
  urgent: boolean;
  important: boolean;
  dueDate?: string;
  startTime?: string;
  endTime?: string;
  recurring: boolean;
  recurringType?: "DAILY" | "WEEKLY" | "MONTHLY";
  createdBy?: TodoUser;
  createdAt?: string;
  client?: TodoUser;
  users?: TodoUser[];
  completed: boolean;
  completedAt?: string;
  archive?: boolean;
}

interface TodoItemProps extends TodoItemData {
  onClick?: () => void;
  isLastItem?: boolean;
  contentInsetX?: string;
  showCheckbox?: boolean;
  onComplete?: (id: number) => void;
}

const formatDateTime = (dateString?: string): { date: string; time: string } | null => {
  if (!dateString) return null;
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return { date: `${day}/${month}/${year}`, time: `${hours}:${minutes}` };
};

const TodoItem = ({
  id,
  title,
  description,
  type,
  timeType,
  urgent,
  important,
  completed,
  dueDate,
  startTime,
  endTime,
  createdBy,
  client,
  users,
  archive,
  onClick,
  isLastItem = false,
  contentInsetX = "var(--mantine-spacing-xl)",
  showCheckbox = true,
  onComplete,
}: TodoItemProps) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const isInteractive = Boolean(onClick);
  const typeLabels = useMemo(() => createTodoTypeLabels(t), [t]);

  const employees = users || [];

  const timeInfo = useMemo(() => {
    if (timeType === "DEADLINE" && dueDate) {
      const formatted = formatDateTime(dueDate);
      if (formatted) return `${t("todoList.common.until")} ${formatted.date} ${formatted.time}`;
    }
    if (timeType === "EXACT_TIME" && startTime) {
      const formatted = formatDateTime(startTime);
      if (formatted) return `${formatted.date} ${formatted.time}`;
    }
    if (timeType === "INTERVAL" && startTime && endTime) {
      const start = formatDateTime(startTime);
      const end = formatDateTime(endTime);
      if (start && end) {
        return start.date === end.date
          ? `${start.date} ${start.time} - ${end.time}`
          : `${start.date} ${start.time} - ${end.date} ${end.time}`;
      }
    }
    return "";
  }, [timeType, dueDate, startTime, endTime, t]);

  const employeesText = employees.map((u) => u.fullName).join(", ");

  const handleComplete = useCallback(() => {
    if (!onComplete) {
      return;
    }

    setIsCompleting(true);
    onComplete(id);
  }, [id, onComplete]);

  return (
    <Box
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        cursor: isInteractive ? "pointer" : "default",
        width: "100%",
        position: "relative",
        // overlap za 1px da hover prekrije prethodnu liniju
        marginTop: -1,
        paddingTop: 1,
        transition: "background-color 150ms ease",
        backgroundColor:
          isInteractive && isHovered ? "var(--mantine-color-gray-1)" : "transparent",
      }}
    >
      {/* TOP inset divider */}
      <Box
        style={{
          position: "absolute",
          left: contentInsetX,
          right: contentInsetX,
          top: 0,
          height: 1,
          backgroundColor: "var(--mantine-color-gray-2)",
          pointerEvents: "none",
        }}
      />

      <Box
        style={{
          paddingLeft: contentInsetX,
          paddingRight: contentInsetX,
          paddingTop: "var(--mantine-spacing-md)",
          paddingBottom: "var(--mantine-spacing-md)",
        }}
      >
        <Group align="flex-start" wrap="nowrap">
          {showCheckbox && (
            <Checkbox
              size="sm"
              checked={completed || isCompleting}
              onChange={handleComplete}
              mt={4}
              onClick={(e) => e.stopPropagation()}
              disabled={archive || !onComplete}
              styles={{ input: { cursor: archive || !onComplete ? "default" : "pointer" } }}
            />
          )}

          <Stack gap={4} style={{ flex: 1 }}>
            <Group gap="xl">
              <Text fw={600} fz={14}>
                {title}
              </Text>
              <Text c="dimmed" fz={14}>
                #{typeLabels[type]}
              </Text>
            </Group>

            {description && (
              <Text c="dimmed" fz={13} lineClamp={1}>
                {description}
              </Text>
            )}

            <Group gap="md" mt={4}>
              <Group gap={4}>
                <IconClock size={14} color="gray" />
                <Text fz={13} c="dimmed">
                  {timeInfo}
                </Text>
              </Group>

              {createdBy && (
                <Group gap={4}>
                  <Text fz={13} c="dimmed">
                    {t("todoList.common.from")}
                  </Text>
                  <Badge variant="light" color="gray" size="sm" radius="sm">
                    {createdBy.fullName}
                  </Badge>
                </Group>
              )}

              {employees.length > 0 && (
                <Group gap={4}>
                  <Text fz={13} c="dimmed">
                    {t("todoList.common.to")}
                  </Text>
                  <Badge variant="light" color="gray" size="sm" radius="sm">
                    {employeesText}
                  </Badge>
                </Group>
              )}
            </Group>
          </Stack>

          <Stack align="flex-end" justify="space-between" style={{ alignSelf: "stretch" }}>
            <Group gap="xs">
              {urgent && (
                <Badge
                  variant="outline"
                  color="red"
                  size="lg"
                  radius="md"
                  leftSection={<IconFlag size={14} color="red" />}
                  styles={{
                    label: { textTransform: "none", color: "black", fontWeight: 400 },
                  }}
                >
                  {t("todoList.priority.urgent")}
                </Badge>
              )}
              {important && (
                <Badge
                  variant="outline"
                  color="yellow"
                  size="lg"
                  radius="md"
                  leftSection={<IconFlag size={14} color="orange" />}
                  styles={{
                    label: { textTransform: "none", color: "black", fontWeight: 400 },
                  }}
                >
                  {t("todoList.priority.important")}
                </Badge>
              )}
            </Group>
            {client && (
              <Link
                to={`${RouteConstants.ROUTE_CLIENTS}/${client.id}`}
                fz={13}
                c="blue"
                onClick={(e) => e.stopPropagation()}
              >
                {client.fullName}
              </Link>
            )}
          </Stack>
        </Group>
      </Box>

      {/* BOTTOM inset divider (samo za poslednji item u grupi) */}
      {isLastItem && (
        <Box
          style={{
            position: "absolute",
            left: contentInsetX,
            right: contentInsetX,
            bottom: 0,
            height: 1,
            backgroundColor: "var(--mantine-color-gray-2)",
            pointerEvents: "none",
          }}
        />
      )}
    </Box>
  );
};

export default TodoItem;
